import { useMemo, useState } from "react";
import { useForm, useFormState } from "react-hook-form";
import { v4 } from "uuid";

import useConditionModalActions from "./useConditionModalActions";

function withAction(conditionGroupId, update, unregister) {
  return ({ id: conditionId, ...c }) => {
    const id = `${conditionGroupId}.${conditionId}`;
    return {
      condition: { ...c, id },
      onRemove: () => {
        update((prev) => {
          return prev.filter(({ id: targetId }) => {
            return `${conditionGroupId}.${targetId}` !== id;
          });
        });
        unregister(id, { keepDirty: true });
      },
    };
  };
}

function getInitialCondition(conditions = []) {
  return conditions?.length
    ? conditions
    : [{ id: v4(), isNew: true, operator: "" }];
}

function formatDataToState(conditionGroupId, conditions, data) {
  return {
    conditionsGroup: {
      ...(conditionGroupId ? { id: conditionGroupId } : {}),
      conditions: conditions.map(({ id, operator }) => {
        return {
          ...data[id].condition,
          id: id,
          operator: operator,
          type: data[id].type.id,
          name: data[id].type.name,
          formField: data[id].condition.formFieldId
            ? {
                name: data[id].condition.formFieldName,
                id: data[id].condition.formFieldId,
              }
            : null,
        };
      }),
    },
  };
}

function formatDataToRequest(conditionsGroupId, conditions, data) {
  return {
    ...(conditionsGroupId ? { conditionsGroupId } : {}),
    conditions: conditions.map(({ id, isNew, operator }) => {
      return {
        ...data[id].condition,
        ...(isNew ? { id: undefined } : { id }),
        operator: operator,
        type: data[id].type.id,
        streams: (data[id].condition.streams || []).map(({ id: streamId }) => {
          return streamId;
        }),
        tags: (data[id].condition.tags || []).map(({ id: tagId }) => {
          return tagId;
        }),
      };
    }),
  };
}

export function useConditionForm(
  workflowId,
  itemId,
  itemType,
  isCreated,
  conditionsGroup,
  conditionTypes,
  onDone
) {
  const conditionGroupId = useMemo(() => {
    return conditionsGroup?.id || v4();
  }, [conditionsGroup]);
  const formProps = useForm({ mode: "onChange" });
  const { isValid, isDirty } = useFormState({ control: formProps.control });
  const [conditions, setConditions] = useState(
    getInitialCondition(conditionsGroup?.conditions)
  );
  const { create, update, remove, isLoading } =
    useConditionModalActions(onDone);

  return {
    formProps: formProps,
    isValid: isValid && isDirty,
    isLoading: isLoading,
    remove: (id) => {
      return isCreated ? remove(id) : onDone({ conditionsGroup: null });
    },
    submit: (data) => {
      if (!isCreated)
        return onDone(
          formatDataToState(
            conditionGroupId,
            conditions,
            data[conditionGroupId]
          )
        );

      const action = conditionsGroup?.id ? update : create;
      return action(
        workflowId,
        itemId,
        formatDataToRequest(
          conditionsGroup?.id,
          conditions,
          data[conditionGroupId]
        ),
        itemType
      );
    },
    group: {
      conditions: conditions.map(
        withAction(conditionGroupId, setConditions, formProps.unregister)
      ),
      conditionTypes: conditionTypes,
      onAdd: (operator) => {
        setConditions((prev) => {
          return prev.concat({ id: v4(), isNew: true, operator: operator });
        });
      },
    },
  };
}

export default useConditionForm;
